import type { components } from "@contracts/generated/editor-service";

type Lineage = components["schemas"]["ProjectLineage"];
type TenantPack = components["schemas"]["TenantPackProvenance"];

export type ProjectSettingsText = {
  type: string;
  /** The "when to choose" sentence the New project dialog uses for this type. */
  chooseWhen: string | null;
  startedFrom: string | null;
  /** The one-line form for the project header. */
  summary: string;
};

/**
 * Tenant pack files win over lineage: such a project's lineage still says
 * `seed`, but its content started from the tenant's own pack.
 */
export function describeProjectSettings({
  tenantPackImport,
  lineage,
}: {
  tenantPackImport?: TenantPack | null | undefined;
  lineage?: Lineage | null | undefined;
}): ProjectSettingsText {
  const version = lineage?.sourceVersion || null;

  if (tenantPackImport) {
    const { tenantPack, packRevision, fileCount } = tenantPackImport;
    const pack = packRevision ? `${tenantPack} pack ${packRevision}` : `${tenantPack} pack`;
    const files = `${fileCount} ${fileCount === 1 ? "file" : "files"}`;
    return {
      type: "Tenant pack files",
      chooseWhen:
        "Choose when the change is for one customer only, in files that customer has its own version of.",
      startedFrom: `${pack}, ${files}${version ? `, on base seed ${version}` : ""}`,
      summary: `${tenantPack} pack files`,
    };
  }

  if (lineage?.origin === "seed") {
    const label = version ? `Base seed ${version}` : "Base seed";
    return {
      type: "Base seed",
      chooseWhen:
        "Choose to try your version for one customer on dev or staging. Other customers and production are not changed.",
      startedFrom: label,
      summary: label,
    };
  }

  if (lineage?.origin === "copied_project") {
    const name = lineage.sourceName || null;
    return {
      type: "Copy of an existing project",
      chooseWhen:
        "Choose to continue from a project you can open, without changing that project.",
      startedFrom:
        (name
          ? `${name} (snapshot taken when this project was created)`
          : "A snapshot taken when this project was created") +
        (version ? `, base ${version}` : ""),
      summary: name ? `Copy of ${name}` : "Copy of an existing project",
    };
  }

  return {
    type: "Not recorded",
    chooseWhen: null,
    startedFrom: null,
    summary: "Origin not recorded",
  };
}
